import React, { useEffect, useState } from 'react'

import { getClaimEmail, PageViewerRole } from '../../hooks/usePageViewerRole'
import { ClaimStatusTypes } from '../../models/ClaimStatus.model'
import { ClaimedPage, ClaimedPagesService } from '../../services/ClaimPages/ClaimedPagesService'
import { WithdrawClaimPageService } from '../../services/ClaimPages/WithdrawClaimPageService'
import { ClaimPageCard } from './ClaimPageCard'
import { PendingClaimCard } from './PendingClaimCard'
import { PendingPublicCard } from './PendingPublicCard'

type PageStatusSectionProps = {
  role: PageViewerRole
  organizationType: 'hospital' | 'agency'
  organizationId: number
  organizationName: string
  claimStatus?: ClaimStatusTypes
  myPageEntry?: ClaimedPage | null
  userEmail?: string
  onClaimStatusChange?: (status: ClaimStatusTypes) => void
}

export const PageStatusSection: React.FC<PageStatusSectionProps> = ({
  role,
  organizationType,
  organizationId,
  organizationName,
  claimStatus,
  myPageEntry,
  userEmail,
  onClaimStatusChange,
}) => {
  const [isWithdrawing, setIsWithdrawing] = useState(false)

  const handleWithdraw = async () => {
    setIsWithdrawing(true)
    try {
      await new WithdrawClaimPageService().withdraw(organizationId, organizationType === 'agency')
      onClaimStatusChange?.(ClaimStatusTypes.UNCLAIMED)
    } catch (err) {
      console.error('Failed to withdraw claim:', err)
    } finally {
      setIsWithdrawing(false)
    }
  }

  if (role === 'owner' && organizationType === 'hospital') {
    return null
  }

  if (role === 'pending_claimant') {
    return (
      <div className="mb-6">
        <PendingClaimCard
          email={getClaimEmail(myPageEntry, userEmail)}
          isWithdrawing={isWithdrawing}
          onWithdraw={handleWithdraw}
        />
      </div>
    )
  }

  if (role === 'pending_public') {
    return (
      <div className="mb-6">
        <PendingPublicCard />
      </div>
    )
  }

  switch (role) {
    case 'claimable':
      return (
        <ClaimPageCard
          organizationType={organizationType}
          organizationId={organizationId}
          organizationName={organizationName}
          initialClaimStatus={claimStatus}
          onStatusChange={onClaimStatusChange}
        />
      )
    case 'verified_public':
    case 'owner':
      return null
    default:
      return null
  }
}

export const useMyPageEntry = (
  pageId: number,
  pageType: 'hospital' | 'agency',
  isAuthenticated: boolean,
  isTokenReady: boolean,
  refreshKey = 0,
): ClaimedPage | null | undefined => {
  const [entry, setEntry] = useState<ClaimedPage | null | undefined>(undefined)

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady) {
      setEntry(null)
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const pages = await new ClaimedPagesService().getMyPages(pageType === 'agency')
        if (cancelled) return
        const match = pages.find((p) => String(p.id) === String(pageId)) ?? null
        setEntry(match)
      } catch (err) {
        console.error('Failed to load my page entry:', err)
        if (!cancelled) setEntry(null)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [pageId, pageType, isAuthenticated, isTokenReady, refreshKey])

  return entry
}
